/**
 * GradMap ↔ CAM authentication (two paths only)
 *
 * PATH 1 — CAM → GradMap login → CAM (inbound OAuth)
 *   User clicks Login on CAM → GradMap BE /api/oauth/authorize → GradMap FE login URL
 *   with ?oauth_redirect=... (query before # in HashRouter).
 *   After successful login: check subscription (paid plan required for students) →
 *   redirect to BE /api/oauth/callback → CAM /auth/callback?code=...
 *   Entry: FormLogin.processLoginSuccess → completeCamInboundOAuth()
 *
 * PATH 2 — GradMap → Open CAM (outbound)
 *   User already on GradMap → openInCam() stores session in localStorage, opens CAM /auth/login.
 *   CAM redirects to GM /api/oauth/authorize → GM login URL with oauth_redirect (bridge tab).
 *   That tab must NOT ask for password: FormLogin seeds session from handoff and calls
 *   completeCamInboundOAuth() → CAM. GradMap UI may block free plans before openInCam.
 *
 * Do not use oauth_nonce on the FE; inbound flow uses oauth_redirect only.
 */
import React from 'react';
import axios from 'axios';
import { get } from 'lodash';
import { STRIPE_PRICE_NAME, USER_ROLE } from 'constants/common';
import {
  clearAuthData,
  getAccessToken,
  getCurrentUserEmail,
  getUserId,
  getUserRole,
  setAccessToken,
  setUserId,
} from 'utils/sessionStorage';

/** Broadcast to CamUpgradeModalHost so it can show the shared "session expired,
 * log in to continue to CAM" dialog. Safe to call from anywhere. */
export function notifyCamSessionExpired(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(CAM_SESSION_EXPIRED_EVENT));
}

/**
 * A token can still be sitting in sessionStorage long after the backend has
 * expired/revoked it (tab left open past the session timeout). Presence
 * alone (`!!getAccessToken()`) is not proof the token still works — CAM's
 * own login would then fail silently with a "valid-looking" but dead token.
 *
 * This pings the backend directly with the current token, bypassing the
 * shared `http` client's interceptors (which would otherwise auto-clear the
 * session and hard-redirect to /public/login on 401 before the CAM button's
 * own handler gets a chance to show a CAM-specific prompt).
 */
export async function isGradMapSessionValid(): Promise<boolean> {
  const token = getAccessToken();
  if (!token) return false;
  try {
    await axios.get(`${process.env.REACT_APP_API_URL}/api/personal_information`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return true;
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 401 || status === 430) {
      return false;
    }
    // Network hiccup or unrelated server error — don't block CAM on this alone.
    return true;
  }
}

/** PATH 2: one-time cross-tab bridge (GM uses per-tab sessionStorage). */
const CAM_OAUTH_HANDOFF_KEY = 'gradmap:cam_oauth_handoff';
const CAM_OAUTH_HANDOFF_TTL_MS = 60_000;

type CamOAuthHandoff = {
  token: string;
  userId?: string;
  userRole?: string;
  studentId?: string;
  currentUserEmail?: string;
  expiresAt: number;
};

export const OAUTH_URL_PARAMS = [
  'oauth_redirect',
  'client_id',
  'response_type',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'return_to',
  'token',
] as const;

/** GradMap → CAM OAuth params may live on the document URL (before `#`) or in the hash query. */
const PENDING_CAM_OAUTH_KEY = 'gradmap:pending_cam_oauth';
/** Avoid wiping a fresh login token when FormLogin remounts mid-OAuth (same `state`). */
const CAM_OAUTH_SESSION_PREP_KEY = 'gradmap:cam_oauth_session_prepared';
const CAM_OAUTH_SESSION_PREP_STATE = 'gradmap:cam_oauth_session_prepared_state';
/** Survives URL replaceState / route re-render so CamUpgradeModal still opens. */
const CAM_UPGRADE_MODAL_FLAG = 'gradmap:cam_upgrade_modal_pending';
/** User chose "Start free trial" — suppress re-opening upgrade modal until oauth completes or flow is cleared. */
const CAM_TRIAL_UPGRADE_FLOW_KEY = 'gradmap:cam_trial_upgrade_flow';

export const CAM_UPGRADE_REQUIRED_EVENT = 'gradmap:cam-upgrade-required';
export const CAM_NAVIGATE_REQUEST_EVENT = 'gradmap:cam-navigate-request';
/** Fired by any "Open CAM" button when the GradMap session is missing/expired.
 * CamUpgradeModalHost (mounted once in index.tsx) listens globally and shows
 * a single shared session-expired dialog instead of each button owning one. */
export const CAM_SESSION_EXPIRED_EVENT = 'gradmap:cam-session-expired';

const CAM_NAVIGATE_PENDING_KEY = 'gradmap:cam_navigate_pending';

type ToastErrorHandler = (message: any) => any;

export function showCamPopupBlockedToast(
  toastError: ToastErrorHandler,
  helpUrl = '/#/public/help/allow-popups',
): void {
  toastError(
    React.createElement(
      'div',
      null,
      'Could not open CAM. Please allow pop-ups for this site. ',
      React.createElement(
        'a',
        {
          href: helpUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: { textDecoration: 'underline', color: 'inherit' },
          onClick: (event: any) => event.stopPropagation(),
        },
        'Learn more',
      ),
    ),
  );
}

export type CamNavigateTarget = 'upgrade-plan' | 'login';

/** Queue in-app navigation for CamOAuthNavigationBridge (inside HashRouter). */
export function requestCamNavigate(target: CamNavigateTarget): void {
  sessionStorage.setItem(CAM_NAVIGATE_PENDING_KEY, target);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(CAM_NAVIGATE_REQUEST_EVENT, { detail: { target } }),
    );
  }
}

export function consumeCamNavigateRequest(): CamNavigateTarget | null {
  const target = sessionStorage.getItem(CAM_NAVIGATE_PENDING_KEY) as CamNavigateTarget | null;
  if (!target) {
    return null;
  }
  sessionStorage.removeItem(CAM_NAVIGATE_PENDING_KEY);
  return target;
}

/** Write OAuth params from a URLSearchParams snapshot (no URL reads). */
function savePendingCamOAuthParams(params: URLSearchParams): void {
  if (!params.get('oauth_redirect')) {
    return;
  }
  const stored: Record<string, string> = {};
  OAUTH_URL_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      stored[key] = value;
    }
  });
  sessionStorage.setItem(PENDING_CAM_OAUTH_KEY, JSON.stringify(stored));
}

/** Read oauth_redirect from document query or hash only (not sessionStorage). */
function readOAuthParamsFromWindowUrl(): URLSearchParams | null {
  const docParams = new URLSearchParams(window.location.search.slice(1));
  if (docParams.get('oauth_redirect')) {
    return docParams;
  }

  const hash = window.location.hash || '';
  if (hash.includes('?')) {
    const hashQuery = hash.split('?').slice(1).join('?');
    const hashParams = new URLSearchParams(hashQuery);
    if (hashParams.get('oauth_redirect')) {
      return hashParams;
    }
  }

  return null;
}

/** Persist OAuth query (lives before `#` in HashRouter apps) across onboarding navigations. */
export function persistPendingCamOAuthParams(): void {
  const fromUrl = readOAuthParamsFromWindowUrl();
  if (fromUrl) {
    savePendingCamOAuthParams(fromUrl);
  }
}

function getStoredCamOAuthParams(): URLSearchParams | null {
  const raw = sessionStorage.getItem(PENDING_CAM_OAUTH_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    const params = new URLSearchParams();
    Object.entries(parsed).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    return params.get('oauth_redirect') ? params : null;
  } catch {
    return null;
  }
}

export function clearPendingCamOAuthParams(): void {
  sessionStorage.removeItem(PENDING_CAM_OAUTH_KEY);
}

export function hasPendingCamOAuthRedirect(): boolean {
  const fromUrl = readOAuthParamsFromWindowUrl();
  if (fromUrl?.get('oauth_redirect')) {
    return true;
  }
  return !!getStoredCamOAuthParams()?.get('oauth_redirect');
}

/** HashRouter: login route may live in the hash while pathname stays `/`. */
export function isCamOAuthLoginRoute(pathname = ''): boolean {
  if (pathname.includes('/public/login')) {
    return true;
  }
  if (typeof window !== 'undefined') {
    return (window.location.hash || '').includes('/public/login');
  }
  return false;
}

/**
 * OAuth params live on the document URL (before `#`). Persist them and land on
 * the login hash route so HashRouter does not bounce to /dashboard first.
 */
export function ensureCamOAuthLoginRoute(): void {
  if (isCamUpgradeModalPending()) {
    return;
  }
  const hash = window.location.hash || '';
  if (
    hash.includes('/upgrade-plan') ||
    hash.includes('/payment') ||
    hash.includes('/success')
  ) {
    return;
  }
  if (isCamTrialUpgradeFlow() && isOnCamBillingRoute()) {
    return;
  }
  if (isCamTrialUpgradeFlow() && !isOnCamBillingRoute()) {
    clearCamTrialUpgradeFlow();
  }
  prepareCamOAuthLoginPage();
  if (!hasPendingCamOAuthRedirect()) {
    return;
  }
  if (hash.includes('/public/login')) {
    return;
  }
  const search = window.location.search || '';
  window.location.replace(`${window.location.pathname}${search}#/public/login`);
}

/** RTK fulfilled action vs raw API body from is-enable-extention. */
export function normalizeSubscriptionResult(subInfo: unknown): Record<string, unknown> {
  const payload = get(subInfo, 'payload');
  if (payload && typeof payload === 'object') {
    return payload as Record<string, unknown>;
  }
  if (subInfo && typeof subInfo === 'object') {
    return subInfo as Record<string, unknown>;
  }
  return {};
}

/** RTK dispatch result vs Redux store shape for subscription info. */
export function getSubscriptionPlanFromResult(subInfo: unknown): string {
  const normalized = normalizeSubscriptionResult(subInfo);
  return String(normalized.subcription_plan ?? '');
}

/** PATH 1 gate: students need a paid plan (not empty / gradmap_starter). Counselor/parent skip. */
export function isPaidPlanForCam(plan: string): boolean {
  const normalized = (plan || '').trim().toLowerCase();
  if (!normalized) return false;
  if (
    normalized === STRIPE_PRICE_NAME.GradMapStarter ||
    normalized.includes('gradmap_starter')
  ) {
    return false;
  }
  return true;
}

export function getUseExtensionFromResult(subInfo: unknown): boolean {
  const normalized = normalizeSubscriptionResult(subInfo);
  return normalized.use_extension === true;
}

/** Starter / free plans: use_extension=false from stripe/is-enable-extention. */
export function isStudentEligibleForCam(subInfo: unknown): boolean {
  const normalized = normalizeSubscriptionResult(subInfo);
  const plan = String(normalized.subcription_plan ?? '').trim().toLowerCase();

  if (!plan || plan.includes('gradmap_starter') || plan === STRIPE_PRICE_NAME.GradMapStarter) {
    return false;
  }

  if (normalized.use_extension !== true) {
    return false;
  }

  return isPaidPlanForCam(plan);
}

function clearCamOAuthSessionPrepFlags(): void {
  sessionStorage.removeItem(CAM_OAUTH_SESSION_PREP_KEY);
  sessionStorage.removeItem(CAM_OAUTH_SESSION_PREP_STATE);
}

export function markCamUpgradeModalPending(): void {
  sessionStorage.setItem(CAM_UPGRADE_MODAL_FLAG, '1');
}

export function isCamUpgradeModalPending(): boolean {
  return sessionStorage.getItem(CAM_UPGRADE_MODAL_FLAG) === '1';
}

/** Block session wipes / hard redirects while CAM inbound OAuth or upgrade gate is active. */
export function shouldSuppressCamSessionInterrupt(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isCamUpgradeModalPending()) {
    return true;
  }
  const onLoginRoute =
    window.location.hash.includes('/public/login') ||
    window.location.pathname.includes('/public/login');
  const oauthInDocument = new URLSearchParams(window.location.search.slice(1)).get(
    'oauth_redirect',
  );
  return (onLoginRoute || !!oauthInDocument) && hasPendingCamOAuthRedirect();
}

export function clearCamUpgradeModalPending(): void {
  sessionStorage.removeItem(CAM_UPGRADE_MODAL_FLAG);
}

export function markCamTrialUpgradeFlow(): void {
  sessionStorage.setItem(CAM_TRIAL_UPGRADE_FLOW_KEY, '1');
}

export function isCamTrialUpgradeFlow(): boolean {
  return sessionStorage.getItem(CAM_TRIAL_UPGRADE_FLOW_KEY) === '1';
}

export function clearCamTrialUpgradeFlow(): void {
  sessionStorage.removeItem(CAM_TRIAL_UPGRADE_FLOW_KEY);
}

/** True when hash is on upgrade / payment / success (HashRouter). */
export function isOnCamBillingRoute(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const hash = window.location.hash || '';
  return (
    hash.includes('/upgrade-plan') ||
    hash.includes('/payment') ||
    hash.includes('/success')
  );
}

/**
 * Suppress re-opening upgrade modal only while user is on billing pages after
 * choosing "Start free trial". Must NOT block modal on the login page.
 */
export function shouldSuppressCamUpgradeNotify(): boolean {
  return isCamTrialUpgradeFlow() && isOnCamBillingRoute();
}

/** Maybe later — drop CAM oauth and modal; caller navigates to /public/login. */
export function dismissCamUpgradeForLater(): void {
  clearCamOAuthFlow();
}

/** Start free trial — close modal only; keep pending oauth for post-upgrade CAM retry. */
export function dismissCamUpgradeForTrial(): void {
  clearCamUpgradeModalPending();
  markCamTrialUpgradeFlow();
}

/** Set durable modal flag first, then notify global host. Never clean URL here (causes remount). */
export function notifyCamUpgradeRequired(onUpgradeRequired?: () => void): void {
  if (shouldSuppressCamUpgradeNotify()) {
    return;
  }
  markCamUpgradeModalPending();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CAM_UPGRADE_REQUIRED_EVENT));
  }
  onUpgradeRequired?.();
}

export type CamOAuthLoginPayload = {
  access_token?: string;
  student_id?: string | number;
  user_role?: string;
};

export type CamOAuthResolveResult = 'redirected' | 'upgrade' | 'skipped';

/**
 * PATH 1 — after password login while CAM oauth_redirect is pending.
 * Uses the fresh access_token from the login response (not session timing).
 */
export async function resolveCamOAuthAfterStudentLogin(options: {
  dispatch: (action: unknown) => Promise<unknown>;
  getStripeSubscriptionInfo: (studentId: string | number) => unknown;
  login: CamOAuthLoginPayload;
}): Promise<CamOAuthResolveResult> {
  console.log('resolveCamOAuthAfterStudentLogin() option:', options);
  persistPendingCamOAuthParams();
  if (!hasPendingCamOAuthRedirect()) {
    return 'skipped';
  }

  if (isCamUpgradeModalPending()) {
    return 'upgrade';
  }

  if (!tryAcquireCamOAuthSubscriptionCheckLock()) {
    return isCamUpgradeModalPending() ? 'upgrade' : 'skipped';
  }

  const releaseLockUnlessUpgradeGate = (): void => {
    if (!isCamUpgradeModalPending() && !hasPendingCamOAuthRedirect()) {
      releaseCamOAuthSubscriptionCheckLock();
    }
  };

  const urlParams = getOAuthUrlParams();
  const oauthRedirect = urlParams.get('oauth_redirect');
  if (!oauthRedirect) {
    releaseCamOAuthSubscriptionCheckLock();
    return 'skipped';
  }

  if (options.login.access_token) {
    setAccessToken(options.login.access_token);
  }
  const token = getAccessToken();
  if (!token) {
    releaseCamOAuthSubscriptionCheckLock();
    return 'skipped';
  }

  const role = String(options.login.user_role ?? '');
  if (role !== USER_ROLE.student) {
    clearCamOAuthFlow();
    redirectToOAuthCallback(token, oauthRedirect, urlParams);
    return 'redirected';
  }

  const rawStudentId = options.login.student_id ?? getUserId();
  if (rawStudentId == null || String(rawStudentId).trim() === '') {
    releaseCamOAuthSubscriptionCheckLock();
    return 'skipped';
  }

  try {
    const subInfo: unknown = await options.dispatch(
      options.getStripeSubscriptionInfo(rawStudentId),
    );
    const requestStatus = get(subInfo, 'meta.requestStatus', '') as string;
    const actionType = get(subInfo, 'type', '') as string;
    const rejected =
      requestStatus === 'rejected' ||
      (typeof actionType === 'string' && actionType.endsWith('/rejected'));
    if (rejected) {
      if (!shouldSuppressCamUpgradeNotify()) {
        notifyCamUpgradeRequired();
      }
      return 'upgrade';
    }
    if (!isStudentEligibleForCam(subInfo)) {
      if (!shouldSuppressCamUpgradeNotify()) {
        notifyCamUpgradeRequired();
      }
      return 'upgrade';
    }
  } catch {
    if (!shouldSuppressCamUpgradeNotify()) {
      notifyCamUpgradeRequired();
    }
    return 'upgrade';
  } finally {
    releaseLockUnlessUpgradeGate();
  }

  clearCamTrialUpgradeFlow();
  clearCamOAuthFlow();
  redirectToOAuthCallback(token, oauthRedirect, urlParams);
  return 'redirected';
}

/** Clear stored OAuth after user dismisses upgrade gate or flow completes. */
export function clearCamOAuthFlow(): void {
  clearPendingCamOAuthParams();
  cleanOAuthParamsFromUrl();
  consumeCamOAuthHandoff();
  clearCamOAuthSessionPrepFlags();
  clearCamUpgradeModalPending();
  clearCamTrialUpgradeFlow();
  releaseCamOAuthSubscriptionCheckLock();
  bumpCamOAuthCompletionEpoch();
}

/**
 * PATH 1 landing: drop stale GradMap session so CAM re-login always requires credentials.
 * Keeps pending oauth_redirect in sessionStorage for post-login completion.
 */
export function clearGradMapSessionForCamOAuthLogin(): void {
  clearAuthData();
}

/**
 * Login page bootstrap for inbound CAM OAuth.
 * PATH 2 (handoff): keep session and auto-complete in FormLogin.
 * PATH 1 (CAM → login): clear stale GM session; user must submit the form.
 */
export function prepareCamOAuthLoginPage(): void {
  persistPendingCamOAuthParams();
  if (isCamUpgradeModalPending()) {
    return;
  }
  if (!hasPendingCamOAuthRedirect()) {
    clearCamOAuthSessionPrepFlags();
    return;
  }
  if (hasCamOAuthHandoff()) {
    return;
  }

  const params = getStoredCamOAuthParams() ?? readOAuthParamsFromWindowUrl();
  const fingerprint = params?.get('state') ?? params?.get('oauth_redirect') ?? '';
  const prepared = sessionStorage.getItem(CAM_OAUTH_SESSION_PREP_KEY) === '1';
  const sameAttempt =
    fingerprint !== '' &&
    sessionStorage.getItem(CAM_OAUTH_SESSION_PREP_STATE) === fingerprint;

  if (prepared && sameAttempt) {
    return;
  }

  clearCamTrialUpgradeFlow();
  clearGradMapSessionForCamOAuthLogin();
  sessionStorage.setItem(CAM_OAUTH_SESSION_PREP_KEY, '1');
  if (fingerprint) {
    sessionStorage.setItem(CAM_OAUTH_SESSION_PREP_STATE, fingerprint);
  }
}

export function getOAuthUrlParams(): URLSearchParams {
  const fromUrl = readOAuthParamsFromWindowUrl();
  if (fromUrl) {
    savePendingCamOAuthParams(fromUrl);
    return fromUrl;
  }

  const stored = getStoredCamOAuthParams();
  if (stored) {
    return stored;
  }

  return new URLSearchParams(window.location.search.slice(1));
}

/** Which deployment tier we're currently running in, used to pick the right CAM URL. */
function resolveCamEnvKey(): 'development' | 'staging' | 'production' {
  const env = (process.env.REACT_APP_ENV || process.env.NODE_ENV || '').toLowerCase();
  const byEnv: Record<string, 'development' | 'staging' | 'production'> = {
    development: 'development',
    dev: 'development',
    local: 'development',
    staging: 'staging',
    stg: 'staging',
    production: 'production',
    prod: 'production',
  };
  if (byEnv[env]) {
    return byEnv[env];
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'development';
    if (host.includes('studentsguidetocollegeplanning')) return 'staging';
  }

  return 'production';
}

/**
 * CAM base URL, one dedicated env var per tier (REACT_APP_CAM_URL_DEVELOPMENT /
 * _STAGING / _PRODUCTION) instead of a single comma-separated REACT_APP_CAM_URL —
 * the comma-separated form was fragile to transfer/copy across environments.
 */
export function getCamBaseUrl(): string {
  const envKey = resolveCamEnvKey();
  const byEnvUrl: Record<'development' | 'staging' | 'production', string | undefined> = {
    development: process.env.REACT_APP_CAM_URL_DEVELOPMENT,
    staging: process.env.REACT_APP_CAM_URL_STAGING,
    production: process.env.REACT_APP_CAM_URL_PRODUCTION,
  };

  const url = byEnvUrl[envKey]?.trim().replace(/\/$/, '');
  if (url) {
    return url;
  }

  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000';
  }

  // DEBUG: surfaces in the browser console so we can tell, from a live
  // deployed bundle, which of the 3 env vars were actually baked in at
  // build time (values are public CAM URLs, not secrets).
  // eslint-disable-next-line no-console
  console.warn(
    '[CAM] No CAM base URL resolved. envKey=%s DEVELOPMENT=%s STAGING=%s PRODUCTION=%s',
    envKey,
    byEnvUrl.development ? 'set' : 'MISSING',
    byEnvUrl.staging ? 'set' : 'MISSING',
    byEnvUrl.production ? 'set' : 'MISSING',
  );

  return '';
}

/** Write a short-lived handoff so a new GM tab can complete CAM OAuth without re-login. */
export function prepareCamOAuthHandoff(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  const payload: CamOAuthHandoff = {
    token,
    userId: getUserId(),
    userRole: getUserRole(),
    studentId: sessionStorage.getItem('session_student_id') ?? undefined,
    currentUserEmail: getCurrentUserEmail(),
    expiresAt: Date.now() + CAM_OAUTH_HANDOFF_TTL_MS,
  };

  localStorage.setItem(CAM_OAUTH_HANDOFF_KEY, JSON.stringify(payload));
  return true;
}

export function peekCamOAuthHandoff(): CamOAuthHandoff | null {
  const raw = localStorage.getItem(CAM_OAUTH_HANDOFF_KEY);
  if (!raw) {
    return null;
  }
  try {
    const payload = JSON.parse(raw) as CamOAuthHandoff;
    if (!payload.token || Date.now() > payload.expiresAt) {
      localStorage.removeItem(CAM_OAUTH_HANDOFF_KEY);
      return null;
    }
    return payload;
  } catch {
    localStorage.removeItem(CAM_OAUTH_HANDOFF_KEY);
    return null;
  }
}

export function hasCamOAuthHandoff(): boolean {
  return peekCamOAuthHandoff() !== null;
}

/** Seed this tab's sessionStorage from PATH 2 handoff (does not remove handoff until success). */
export function trySeedSessionFromCamOAuthHandoff(): boolean {
  const payload = peekCamOAuthHandoff();
  if (!payload) {
    return false;
  }

  setAccessToken(payload.token);
  if (payload.userId) setUserId(payload.userId);
  if (payload.userRole) sessionStorage.setItem('userRole', payload.userRole);
  if (payload.studentId) sessionStorage.setItem('session_student_id', payload.studentId);
  if (payload.currentUserEmail) {
    sessionStorage.setItem('currentUserEmail', payload.currentUserEmail);
  }
  return true;
}

export function consumeCamOAuthHandoff(): void {
  localStorage.removeItem(CAM_OAUTH_HANDOFF_KEY);
}

export function clearCamOAuthHandoff(): void {
  consumeCamOAuthHandoff();
}

let camOAuthCompletionEpoch = 0;
/** Prevents repeated stripe/is-enable-extention calls while upgrade modal is open or check is in flight. */
let camOAuthSubscriptionCheckLocked = false;

function bumpCamOAuthCompletionEpoch(): void {
  camOAuthCompletionEpoch += 1;
}

function releaseCamOAuthSubscriptionCheckLock(): void {
  camOAuthSubscriptionCheckLocked = false;
}

/** One subscription check per inbound OAuth attempt until flow completes or is cleared. */
function tryAcquireCamOAuthSubscriptionCheckLock(): boolean {
  if (camOAuthSubscriptionCheckLocked || isCamUpgradeModalPending()) {
    return false;
  }
  camOAuthSubscriptionCheckLocked = true;
  return true;
}

/** Bust in-flight OAuth completion (stale session on mount vs fresh login submit). */
export function resetCamOAuthCompletionLock(): void {
  bumpCamOAuthCompletionEpoch();
}

/** CAM in-app path after OAuth (e.g. Requirements Step 4). */
export function buildCamRequirementsReturnTo(
  studentExternalId: string,
  processExternalId: string,
): string {
  return `/student/${studentExternalId}/application-process/${processExternalId}/requirements`;
}

export function buildCamLoginUrl(returnTo = '/'): string {
  const base = getCamBaseUrl();
  if (!base) {
    throw new Error('REACT_APP_CAM_URL is not configured');
  }
  const url = new URL(`${base}/auth/login`);
  url.searchParams.set('return_to', returnTo);
  return url.toString();
}

/**
 * PATH 2 — Open CAM from GradMap (new tab + session handoff, no login form on GM).
 * Returns false if popup was blocked or no GM session exists.
 */
export function openInCam(returnTo = '/'): boolean {
  if (!prepareCamOAuthHandoff()) return false;

  // Clear any leftover OAuth state from a previous attempt (e.g. PATH 1 trial flow).
  // The new tab inherits this tab's sessionStorage; a stale `state` here would be
  // replayed to CAM's callback and trigger "State mismatch - possible CSRF attack".
  // PATH 2 obtains fresh oauth params (incl. a new state) from CAM via the new tab URL.
  clearPendingCamOAuthParams();
  clearCamOAuthSessionPrepFlags();
  clearCamUpgradeModalPending();
  clearCamTrialUpgradeFlow();

  // No noopener: same-site GM login tab must read localStorage handoff from this origin.
  let openCamUrl = buildCamLoginUrl(returnTo);
  console.log('openCamUrl:', openCamUrl);
  const opened = window.open(openCamUrl, '_blank');
  if (!opened) {
    clearCamOAuthHandoff();
    return false;
  }
  return true;
}

export function cleanOAuthParamsFromUrl(): void {
  const url = new URL(window.location.href);
  OAUTH_URL_PARAMS.forEach((p) => url.searchParams.delete(p));

  if (url.hash.includes('?')) {
    const [hashPath, hashQuery = ''] = url.hash.split('?');
    const hashParams = new URLSearchParams(hashQuery);
    OAUTH_URL_PARAMS.forEach((p) => hashParams.delete(p));
    const remaining = hashParams.toString();
    url.hash = remaining ? `${hashPath}?${remaining}` : hashPath;
  }

  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function buildOAuthCallbackUrl(
  token: string,
  oauthRedirect: string,
  urlParams?: URLSearchParams,
): string {
  const callbackUrl = new URL(`${process.env.REACT_APP_API_URL}/api/oauth/callback`);
  callbackUrl.searchParams.set('token', token);
  callbackUrl.searchParams.set('oauth_redirect', oauthRedirect);

  const passThroughParams = [
    'client_id',
    'response_type',
    'redirect_uri',
    'code_challenge',
    'code_challenge_method',
    'state',
    'return_to',
  ];

  const params = urlParams ?? getOAuthUrlParams();
  for (const param of passThroughParams) {
    const value = params.get(param);
    if (value) callbackUrl.searchParams.set(param, value);
  }

  return callbackUrl.toString();
}

export function redirectToOAuthCallback(
  token: string,
  oauthRedirect: string,
  urlParams?: URLSearchParams,
): void {
  window.location.href = buildOAuthCallbackUrl(token, oauthRedirect, urlParams);
}

export type OAuthGateOptions = {
  dispatch: (action: unknown) => Promise<unknown>;
  getStripeSubscriptionInfo: (studentId: string | number) => unknown;
  personalStore?: { user_role?: string; student_id?: string | number };
  /** Fresh login response — preferred over sessionStorage for PATH 1. */
  session?: { user_role?: string; student_id?: string | number };
  onUpgradeRequired: () => void;
  onRedirecting?: () => void;
};

export type CamOAuthAfterLoginResult = 'redirected' | 'upgrade' | 'failed';

/**
 * PATH 1 — explicit post-login gate (form submit). Does not use completion epoch / in-flight locks.
 */
export async function gateCamOAuthAfterLogin(
  options: OAuthGateOptions & {
    session: { user_role?: string; student_id?: string | number };
  },
): Promise<CamOAuthAfterLoginResult> {
  persistPendingCamOAuthParams();

  const urlParams = getOAuthUrlParams();
  const oauthRedirect = urlParams.get('oauth_redirect');
  if (!oauthRedirect) {
    return 'failed';
  }

  const token = getAccessToken();
  if (!token) {
    return 'failed';
  }

  const { dispatch, getStripeSubscriptionInfo, session, onUpgradeRequired, onRedirecting } =
    options;

  const role = String(session.user_role ?? '');
  if (role !== USER_ROLE.student) {
    onRedirecting?.();
    clearCamOAuthFlow();
    redirectToOAuthCallback(token, oauthRedirect, urlParams);
    return 'redirected';
  }

  const studentId =
    session.student_id != null && String(session.student_id).trim() !== ''
      ? String(session.student_id)
      : undefined;
  if (!studentId) {
    return 'failed';
  }

  try {
    const subInfo: unknown = await dispatch(getStripeSubscriptionInfo(studentId));
    const rejected = get(subInfo, 'type', '') as string;
    if (typeof rejected === 'string' && rejected.endsWith('/rejected')) {
      notifyCamUpgradeRequired(onUpgradeRequired);
      return 'upgrade';
    }

    if (!isStudentEligibleForCam(subInfo)) {
      notifyCamUpgradeRequired(onUpgradeRequired);
      return 'upgrade';
    }
  } catch {
    notifyCamUpgradeRequired(onUpgradeRequired);
    return 'upgrade';
  }

  onRedirecting?.();
  clearCamOAuthFlow();
  redirectToOAuthCallback(token, oauthRedirect, urlParams);
  return 'redirected';
}

/**
 * PATH 1 — Complete inbound CAM OAuth when URL has oauth_redirect and GM session exists.
 * Checks subscription for students; counselors/parents go straight to CAM.
 * Also used after PATH 2 handoff seeds session in a new tab (no password).
 */
export async function completeCamInboundOAuth(options: OAuthGateOptions): Promise<boolean> {
  const urlParams = getOAuthUrlParams();
  const oauthRedirect = urlParams.get('oauth_redirect');

  if (!oauthRedirect) return false;

  if (isCamUpgradeModalPending()) {
    return true;
  }

  if (!tryAcquireCamOAuthSubscriptionCheckLock()) {
    return isCamUpgradeModalPending();
  }

  const epoch = camOAuthCompletionEpoch;

  const finish = (completed: boolean, run?: () => void): boolean => {
    if (epoch !== camOAuthCompletionEpoch) {
      return false;
    }
    run?.();
    return completed;
  };

  let token = getAccessToken();
  if (!token) {
    trySeedSessionFromCamOAuthHandoff();
    token = getAccessToken();
  }

  if (!token) {
    releaseCamOAuthSubscriptionCheckLock();
    return false;
  }

  const {
    dispatch,
    getStripeSubscriptionInfo,
    personalStore,
    session,
    onUpgradeRequired,
    onRedirecting,
  } = options;

  const releaseLockUnlessUpgradeGate = (): void => {
    if (!isCamUpgradeModalPending() && !hasPendingCamOAuthRedirect()) {
      releaseCamOAuthSubscriptionCheckLock();
    }
  };

  const role = (
    session?.user_role ??
    sessionStorage.getItem('userRole') ??
    personalStore?.user_role ??
    ''
  ).toString();
  const isNonStudent = role !== '' && role !== USER_ROLE.student;

  if (isNonStudent) {
    return finish(true, () => {
      onRedirecting?.();
      clearCamOAuthFlow();
      redirectToOAuthCallback(token!, oauthRedirect, urlParams);
    });
  }

  const rawStudentId =
    session?.student_id ??
    sessionStorage.getItem('session_student_id') ??
    personalStore?.student_id;
  const studentId =
    rawStudentId != null && String(rawStudentId).trim() !== ''
      ? String(rawStudentId)
      : undefined;

  if (!studentId) {
    releaseCamOAuthSubscriptionCheckLock();
    return false;
  }

  try {
    const subInfo: unknown = await dispatch(getStripeSubscriptionInfo(studentId));
    if (epoch !== camOAuthCompletionEpoch) {
      releaseCamOAuthSubscriptionCheckLock();
      return false;
    }
    const requestStatus = get(subInfo, 'meta.requestStatus', '') as string;
    const actionType = get(subInfo, 'type', '') as string;
    const rejected =
      requestStatus === 'rejected' ||
      (typeof actionType === 'string' && actionType.endsWith('/rejected'));
    if (rejected) {
      if (shouldSuppressCamUpgradeNotify()) {
        return finish(false);
      }
      return finish(true, () => notifyCamUpgradeRequired(onUpgradeRequired));
    }
    if (!isStudentEligibleForCam(subInfo)) {
      if (shouldSuppressCamUpgradeNotify()) {
        return finish(false);
      }
      return finish(true, () => notifyCamUpgradeRequired(onUpgradeRequired));
    }
  } catch {
    if (shouldSuppressCamUpgradeNotify()) {
      releaseLockUnlessUpgradeGate();
      return finish(false);
    }
    return finish(true, () => notifyCamUpgradeRequired(onUpgradeRequired));
  } finally {
    releaseLockUnlessUpgradeGate();
  }

  return finish(true, () => {
    onRedirecting?.();
    clearCamTrialUpgradeFlow();
    clearCamOAuthFlow();
    redirectToOAuthCallback(token!, oauthRedirect, urlParams);
  });
}

/** @deprecated Use completeCamInboundOAuth — kept for existing imports */
export const gateThenRedirectToCam = completeCamInboundOAuth;
